import { Cluster } from "@googlemaps/markerclusterer";
import type { Circle, Geometry, PointOfInterest } from "../../../@types/mapviewxs";
import { STACKED_CLUSTER_PRECISION, UNKNOWN_POSITION } from "../const";
import type { LatLng, LatLngBounds, LatLngLiteral, MarkerWithCircle } from "../types";
import { getBoundsZoomLevel } from "./bounds";

export const calculateGoogleMapsZoomLevel = (mapElement: HTMLElement, center: LatLngLiteral, radiusInMeters?: number) => {
  if (!radiusInMeters) return null;

  //create bounds from center and radius
  const bounds = new google.maps.LatLngBounds();
  const radiusInKm = radiusInMeters / 1000;
  const radiusInDegrees = radiusInKm / 111.2;

  const sw = new google.maps.LatLng(center.lat - radiusInDegrees, center.lng - radiusInDegrees);
  const ne = new google.maps.LatLng(center.lat + radiusInDegrees, center.lng + radiusInDegrees);
  bounds.extend(sw);
  bounds.extend(ne);

  const mapDim = mapElement.getBoundingClientRect();
  return getBoundsZoomLevel(bounds, mapDim);
};

export const calculateRadiusForVisibleRegion = (bounds: LatLngBounds): string => {
  const kmPerLatitudeDegree = 111.2;
  const maxDistance = 200;
  const minDistance = 3;

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const center = positionToLiteral(bounds.getCenter());

  const latDelta = ne.lat() - sw.lat();
  const lngDelta = ne.lng() - sw.lng();

  const latDistance = latDelta * kmPerLatitudeDegree;
  const longDistance = Math.cos((Math.floor(center.lat) * Math.PI) / 180) * lngDelta * kmPerLatitudeDegree;
  const distance = Math.min(latDistance, longDistance);
  const radius = Math.min(maxDistance, Math.max(minDistance, Math.round(Math.floor(distance) / 2)));

  return String(radius);
};

export const extractPosition = (model?: Geometry | Circle): LatLngLiteral => {
  if (model) {
    // Circle
    if ("center" in model) {
      return {
        lat: model.center.latitude,
        lng: model.center.longitude,
      };
    }

    if (model.point) {
      // Geometry
      return {
        lat: model.point.latitude,
        lng: model.point.longitude,
      };
    }

    if (model.circle?.center) {
      // Geometry
      return {
        lat: model.circle.center.latitude,
        lng: model.circle.center.longitude,
      };
    }
  }

  return UNKNOWN_POSITION;
};

export const calculateMinMaxLatitude = (points?: PointOfInterest[]) => {
  const minLatitude = points?.reduce((min, item) => Math.min(min, item?.geometry?.circle?.center?.latitude ?? 0), Infinity) ?? 0;
  const maxLatitude = points?.reduce((max, item) => Math.max(max, item?.geometry?.circle?.center?.latitude ?? 0), -Infinity) ?? 0;

  return { minLatitude, maxLatitude };
};

export const latRad = (lat: number): number => {
  const sin = Math.sin((lat * Math.PI) / 180);
  const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;

  return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
};

export const positionToLiteral = (position: LatLng | LatLngLiteral | null | undefined): LatLngLiteral => {
  if (position && "lat" in position && "lng" in position) {
    if (typeof position.lat === "function" && typeof position.lng === "function") {
      return {
        lat: position.lat(),
        lng: position.lng(),
      };
    }

    return position as LatLngLiteral;
  }

  return UNKNOWN_POSITION;
};

export const isPointInCluster =
  (cluster: Cluster) =>
  (poi: PointOfInterest): boolean => {
    const { latitude, longitude } = poi.geometry.circle?.center || {};
    if (!latitude || !longitude) return false;

    const clusterPosition = positionToLiteral(cluster.position);
    return isSamePlace(clusterPosition, { lat: latitude, lng: longitude });
  };

export const areMarkersInSameCoordinates = (cluster: Cluster) =>
  cluster.markers?.every((marker) => isSamePlace((cluster as any).markers[0].position, (marker as any).position, STACKED_CLUSTER_PRECISION));

export const isSameBound = (bound1: LatLngBounds, bound2: LatLngBounds, precision = 2.5): boolean =>
  isSamePlace(positionToLiteral(bound1.getCenter()), positionToLiteral(bound2.getCenter()), precision);

export const isSamePlace = (pos1: LatLngLiteral, pos2: LatLngLiteral, precision = 2.5): boolean =>
  pos1.lat.toFixed(precision) === pos2.lat.toFixed(precision) && pos1.lng.toFixed(precision) === pos2.lng.toFixed(precision);

export const getVisiblePointsOfInterest = (
  pointsOfInterest: PointOfInterest[],
  points: MarkerWithCircle[],
  bounds?: google.maps.LatLngBounds,
): PointOfInterest[] => {
  if (!bounds || pointsOfInterest.length === 0 || points.length === 0) return [];

  const markersInBounds = points.filter((point) => {
    const position = (point.marker as google.maps.marker.AdvancedMarkerElement).position;
    return position && bounds.contains(position);
  });

  const markersInBoundsIds = markersInBounds.map((point) => parseInt((point.marker as google.maps.marker.AdvancedMarkerElement).id));

  return pointsOfInterest.filter((_, index) => markersInBoundsIds.includes(index));
};

export const getLocationChangeText = (zipcode?: string, distance?: string): string | undefined =>
  [zipcode, distance].filter(Boolean).join(" · ") || undefined;

export const getListingId = (poi?: PointOfInterest) => poi?.itemCard?.card.listingId ?? "";
